// ============================================================================
// UserProfilePage.jsx — Zenna Public User Profile
// Portfolio, follow/unfollow, social links, seller info, referral
// ============================================================================

import { useState, useEffect } from 'react'
import ShareButtons from './ShareButtons'
import ZennaPageHeader from './ZennaPageHeader'

const API_URL = import.meta.env.VITE_API_URL || ''

// ============================================================================
// EDIT PROFILE MODAL
// ============================================================================

function EditProfileModal({ profile, userEmail, onClose, onSaved }) {
  const [form, setForm] = useState({
    display_name: profile?.display_name || '',
    bio: profile?.bio || '',
    location: profile?.location || '',
    website: profile?.website || '',
    craft_specialties: (profile?.craft_specialties || []).join(', '),
    show_watermark: profile?.show_watermark ?? true
  })
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await fetch(`${API_URL}/profiles`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail },
        body: JSON.stringify({
          ...form,
          craft_specialties: form.craft_specialties.split(',').map(s => s.trim()).filter(Boolean)
        })
      })
      const data = await res.json()
      if (data.success) {
        if (onSaved) onSaved(data.profile)
        onClose()
      }
    } catch (err) {
      console.error('Save profile error:', err)
    }
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-slate-700" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Edit Profile</h2>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Display Name</label>
            <input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              maxLength={50} className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-zenna-violet/50" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Bio</label>
            <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
              maxLength={500} rows={3} className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-zenna-violet/50 resize-none" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Location</label>
            <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              placeholder="City, State" maxLength={100} className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-zenna-violet/50" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Website</label>
            <input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
              placeholder="https://" maxLength={200} className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-zenna-violet/50" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Craft Specialties (comma separated)</label>
            <input value={form.craft_specialties} onChange={e => setForm(f => ({ ...f, craft_specialties: e.target.value }))}
              placeholder="cross-stitch, embroidery, vinyl" className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-zenna-violet/50" />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
            <input type="checkbox" checked={form.show_watermark} onChange={e => setForm(f => ({ ...f, show_watermark: e.target.checked }))}
              className="rounded border-slate-600 bg-slate-700 text-zenna-violet focus:ring-zenna-violet" />
            Default "Made with Zenna" watermark on shares
          </label>
        </div>
        <div className="p-5 border-t border-slate-700 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium text-slate-400 bg-slate-700/50 hover:bg-slate-700">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-zenna-violet to-zenna-cyan hover:opacity-90 disabled:opacity-30">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// CONNECT PINTEREST BUTTON
// ============================================================================

function ConnectPinterestButton({ userEmail }) {
  const [status, setStatus] = useState(null) // null, 'connected', 'disconnected'
  const [connecting, setConnecting] = useState(false)

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/profiles/pinterest/status?email=${userEmail}`, {
          headers: { 'x-user-email': userEmail }
        })
        const data = await res.json()
        setStatus(data.connected ? 'connected' : 'disconnected')
      } catch { setStatus('disconnected') }
    }
    if (userEmail) checkStatus()
  }, [userEmail])

  const handleConnect = async () => {
    setConnecting(true)
    try {
      const res = await fetch(`${API_URL}/profiles/pinterest/connect?email=${userEmail}`, {
        headers: { 'x-user-email': userEmail }
      })
      const data = await res.json()
      if (data.auth_url) window.open(data.auth_url, '_blank', 'width=600,height=700')
    } catch (err) { console.error(err) }
    setConnecting(false)
  }

  if (status === 'connected') {
    return (
      <div className="flex items-center gap-2 text-xs text-green-400 bg-green-400/10 px-3 py-1.5 rounded-lg">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
        Pinterest Connected ✓
      </div>
    )
  }

  return (
    <button
      onClick={handleConnect}
      disabled={connecting}
      className="flex items-center gap-2 text-xs text-[#E60023] bg-[#E60023]/10 hover:bg-[#E60023]/20 px-3 py-1.5 rounded-lg border border-[#E60023]/20 transition-colors"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
      {connecting ? 'Connecting...' : 'Connect Pinterest'}
    </button>
  )
}

// ============================================================================
// MAIN PROFILE PAGE
// ============================================================================

export default function UserProfilePage({ userId, currentUserEmail = '', isOwnProfile = false }) {
  const [profile, setProfile] = useState(null)
  const [portfolio, setPortfolio] = useState([])
  const [loading, setLoading] = useState(true)
  const [showEditModal, setShowEditModal] = useState(false)
  const [referralCopied, setReferralCopied] = useState(false)
  const [tab, setTab] = useState('portfolio') // portfolio, listings, about

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true)
      try {
        const endpoint = isOwnProfile
          ? `${API_URL}/profiles/me/profile`
          : `${API_URL}/profiles/${userId}`

        const res = await fetch(endpoint, {
          headers: currentUserEmail ? { 'x-user-email': currentUserEmail } : {}
        })
        const data = await res.json()
        setProfile(data.profile)

        // Load portfolio
        const targetId = data.profile?.user_id || userId
        const portfolioRes = await fetch(`${API_URL}/profiles/${targetId}/portfolio`)
        const portfolioData = await portfolioRes.json()
        setPortfolio(portfolioData.posts || [])
      } catch (err) {
        console.error('Load profile error:', err)
      }
      setLoading(false)
    }
    loadProfile()
  }, [userId, currentUserEmail, isOwnProfile])

  const handleFollow = async () => {
    if (!currentUserEmail || !profile) return
    try {
      const isFollowing = profile.is_following
      const method = isFollowing ? 'DELETE' : 'POST'
      await fetch(`${API_URL}/community/follow/${profile.user_id}`, {
        method,
        headers: { 'x-user-email': currentUserEmail }
      })
      setProfile(prev => ({
        ...prev,
        is_following: !isFollowing,
        follower_count: (prev.follower_count || 0) + (isFollowing ? -1 : 1)
      }))
    } catch (err) { console.error(err) }
  }

  const copyReferral = () => {
    if (!profile?.referral_code) return
    navigator.clipboard.writeText(`https://zennago.com/join?ref=${profile.referral_code}`)
    setReferralCopied(true)
    setTimeout(() => setReferralCopied(false), 2000)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-zenna-violet border-t-transparent rounded-full"></div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <p className="text-slate-500">Profile not found</p>
      </div>
    )
  }

  const BADGE_MAP = {
    founding: { label: '⭐ Founding Member', color: 'bg-amber-500/20 text-amber-400' },
    creator: { label: '🎨 Creator', color: 'bg-purple-500/20 text-purple-400' },
    pro: { label: '⚡ Pro', color: 'bg-blue-500/20 text-blue-400' },
    crafter: { label: '✂️ Crafter', color: 'bg-green-500/20 text-green-400' }
  }

  const badge = BADGE_MAP[profile.badge] || BADGE_MAP.crafter

  return (
    <div className="min-h-screen bg-slate-900">
      <ZennaPageHeader title="Profile" />
      {/* Cover + Avatar */}
      <div className="relative">
        <div className="h-48 bg-gradient-to-r from-zenna-violet/30 via-slate-800 to-zenna-cyan/30">
          {profile.cover_image_url && (
            <img src={profile.cover_image_url} alt="" className="w-full h-full object-cover" />
          )}
        </div>

        <div className="max-w-4xl mx-auto px-4 relative">
          <div className="absolute -bottom-16 left-4">
            {profile.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="w-32 h-32 rounded-full border-4 border-slate-900 object-cover" />
            ) : (
              <div className="w-32 h-32 rounded-full border-4 border-slate-900 bg-gradient-to-br from-zenna-violet to-zenna-cyan flex items-center justify-center text-4xl text-white font-bold">
                {(profile.display_name || '?')[0].toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Info */}
      <div className="max-w-4xl mx-auto px-4 pt-20 pb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">{profile.display_name}</h1>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${badge.color}`}>{badge.label}</span>
              {profile.is_seller && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-zenna-emerald/20 text-zenna-emerald">🛒 Seller</span>
              )}
            </div>
            {profile.location && (
              <p className="text-sm text-slate-500 mt-0.5">📍 {profile.location}</p>
            )}
            {profile.bio && (
              <p className="text-sm text-slate-400 mt-2 max-w-lg">{profile.bio}</p>
            )}
            {profile.craft_specialties?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {profile.craft_specialties.map(s => (
                  <span key={s} className="text-[10px] text-zenna-cyan/80 bg-zenna-cyan/10 px-2 py-0.5 rounded-full">{s}</span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isOwnProfile ? (
              <button
                onClick={() => setShowEditModal(true)}
                className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-xl border border-slate-700"
              >
                Edit Profile
              </button>
            ) : currentUserEmail ? (
              <button
                onClick={handleFollow}
                className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                  profile.is_following
                    ? 'text-slate-300 bg-slate-800 hover:bg-slate-700 border border-slate-700'
                    : 'text-white bg-gradient-to-r from-zenna-violet to-zenna-cyan hover:opacity-90'
                }`}
              >
                {profile.is_following ? 'Following' : 'Follow'}
              </button>
            ) : null}
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-6 py-3 border-y border-slate-800">
          <div className="text-center">
            <div className="text-lg font-bold text-white">{profile.gallery_count || 0}</div>
            <div className="text-[10px] text-slate-500">Posts</div>
          </div>
          <div className="text-center cursor-pointer hover:opacity-80">
            <div className="text-lg font-bold text-white">{profile.follower_count || 0}</div>
            <div className="text-[10px] text-slate-500">Followers</div>
          </div>
          <div className="text-center cursor-pointer hover:opacity-80">
            <div className="text-lg font-bold text-white">{profile.following_count || 0}</div>
            <div className="text-[10px] text-slate-500">Following</div>
          </div>
          {profile.is_seller && (
            <div className="text-center">
              <div className="text-lg font-bold text-white">{profile.total_sales || 0}</div>
              <div className="text-[10px] text-slate-500">Sales</div>
            </div>
          )}
        </div>

        {/* Own profile extras */}
        {isOwnProfile && (
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <ConnectPinterestButton userEmail={currentUserEmail} />

            {profile.referral_code && (
              <button
                onClick={copyReferral}
                className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border transition-all ${
                  referralCopied
                    ? 'text-green-400 bg-green-400/10 border-green-400/30'
                    : 'text-slate-400 bg-slate-800 border-slate-700 hover:text-white'
                }`}
              >
                🔗 {referralCopied ? 'Copied!' : `Referral: ${profile.referral_code}`}
              </button>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mt-6 bg-slate-800 rounded-lg p-0.5 w-fit">
          {['portfolio', 'about'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${
                tab === t ? 'bg-zenna-violet/20 text-zenna-violet' : 'text-slate-400 hover:text-white'
              }`}
            >
              {t === 'portfolio' ? '🎨 Portfolio' : '📋 About'}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="mt-6">
          {tab === 'portfolio' && (
            portfolio.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-3xl mb-2">🎨</p>
                <p className="text-sm text-slate-500">
                  {isOwnProfile ? "You haven't shared any designs yet." : 'No designs shared yet.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {portfolio.map(post => (
                  <div key={post.id} className="group relative rounded-xl overflow-hidden border border-slate-700/50 hover:border-slate-600/50 transition-all cursor-pointer">
                    <img src={post.thumbnail_url || post.image_urls?.[0]} alt={post.title} className="w-full aspect-square object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-end">
                      <div className="p-2 opacity-0 group-hover:opacity-100 transition-opacity w-full">
                        <p className="text-xs text-white font-medium truncate">{post.title}</p>
                        <p className="text-[10px] text-slate-300">❤️ {post.like_count} · 💬 {post.comment_count}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === 'about' && (
            <div className="space-y-4 max-w-lg">
              {profile.bio && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase mb-1">Bio</h3>
                  <p className="text-sm text-slate-300">{profile.bio}</p>
                </div>
              )}
              {profile.website && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase mb-1">Website</h3>
                  <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-sm text-zenna-cyan hover:underline">{profile.website}</a>
                </div>
              )}
              {profile.social_links && Object.keys(profile.social_links).length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase mb-1">Social</h3>
                  <div className="flex gap-2">
                    {profile.social_links.pinterest && (
                      <a href={`https://pinterest.com/${profile.social_links.pinterest}`} target="_blank" rel="noopener" className="text-[#E60023] text-sm hover:underline">Pinterest</a>
                    )}
                    {profile.social_links.instagram && (
                      <a href={`https://instagram.com/${profile.social_links.instagram}`} target="_blank" rel="noopener" className="text-[#E4405F] text-sm hover:underline">Instagram</a>
                    )}
                    {profile.social_links.etsy && (
                      <a href={profile.social_links.etsy} target="_blank" rel="noopener" className="text-[#F56400] text-sm hover:underline">Etsy</a>
                    )}
                  </div>
                </div>
              )}
              <div>
                <h3 className="text-xs font-semibold text-slate-400 uppercase mb-1">Joined</h3>
                <p className="text-sm text-slate-300">{new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit modal */}
      {showEditModal && (
        <EditProfileModal
          profile={profile}
          userEmail={currentUserEmail}
          onClose={() => setShowEditModal(false)}
          onSaved={(updated) => setProfile(updated)}
        />
      )}
    </div>
  )
}